import apiV2 from '../api/v2';
import { parseBlobExportError } from '../utils/blobError';
import { iteroPath } from './iteroConnection';

// Mirrors backend/src/routes/api/v2/practices/itero/itero.browse.schemas.ts and .service.ts.

export type IteroGender = 'male' | 'female';

export interface IteroPatient {
    id: string;
    firstName: string;
    lastName: string;
    chartNumber: string;
    dateOfBirth: string | null;
    gender: IteroGender | string | null;
    creationDate: string | null;
    updatedDate: string | null;
    lastScanDate: string | null;
}

export interface IteroPatientPage {
    patients: IteroPatient[];
    total: number;
    count: number;
    nextOffset: number | null;
}

export type IteroPatientSortField =
    'FirstName' | 'LastName' | 'ChartNumber' | 'UpdatedDate' | 'LastScanDate';

export type IteroSortOrder = 'asc' | 'desc';

export interface IteroPatientsParams {
    firstName?: string;
    lastName?: string;
    chartNumber?: string;
    lastScanDateFrom?: string;
    sort: IteroPatientSortField;
    order: IteroSortOrder;
    offset: number;
    limit: number;
}

export type IteroPatientsFilterParams = Omit<IteroPatientsParams, 'offset'>;

export const ITERO_ORDER_STATUSES = [
    'Completed',
    'iTero Modeling',
    'Lab Review',
    'Scanning',
    'iTero Milling',
    'Exporting to Doctor Site',
    'Align Production'
] as const;

export type IteroOrderStatus = (typeof ITERO_ORDER_STATUSES)[number] | (string & {});

export interface IteroPrep {
    adaId: number | null;
    unitTypeId: number | null;
    unitTypeName: string | null;
    bridgeId: number | null;
}

export interface IteroOrder {
    rxId: string;
    orderId: number;
    orderCode: string;
    procedureName: string | null;
    procedureTypeName: string | null;
    caseType: string | null;
    status: IteroOrderStatus;
    scanningDate: string | null;
    scanningTimeSeconds: number | null;
    doctorId: number | null;
    doctorName: string | null;
    doctorLicenseNumber: string | null;
    scanningOperatorId: number | null;
    scanningOperatorName: string | null;
    patientFirstName: string;
    patientLastName: string;
    patientChartNumber: string;
    companyId: number | null;
    companyName: string | null;
    labId: number | null;
    labName: string | null;
    preps: IteroPrep[];
    notes: string | null;
    dueDate: string | null;
    viewerUrl: string | null;
    scannerModelId: number | null;
    scannerModelDescription: string | null;
}

export interface IteroOrderPage {
    orders: IteroOrder[];
    hasMore: boolean;
    offset: number;
    pageSize: number;
}

export interface IteroOrdersParams {
    offset: number;
    pageSize: number;
    dateFrom?: string;
    dateTo?: string;
    chartNumber?: string;
}

export type IteroOrdersFilterParams = Omit<IteroOrdersParams, 'offset'>;

// The backend passes these blocks through loosely, so every field is optional here.
export interface IteroFullRxPatient {
    uid?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    chartNumber?: string | null;
    dateOfBirth?: string | null;
    gender?: string | null;
    dateUpdated?: string | null;
}

export interface IteroFullRxOrder {
    id?: number | null;
    code?: string | null;
    caseTypeId?: number | null;
    procedureId?: number | null;
    procedureMapId?: number | null;
    procedureTypeId?: number | null;
    scanDate?: string | null;
    dueDate?: string | null;
    shipToId?: number | null;
    shipToName?: string | null;
    directToLab?: boolean | null;
    hasNiri?: boolean | null;
}

export interface IteroFullRxDoctor {
    id?: number | null;
    name?: string | null;
    licence?: string | null;
}

export interface IteroFullRx {
    version: number | null;
    id: string | null;
    companyId: number | null;
    patient: IteroFullRxPatient | null;
    order: IteroFullRxOrder | null;
    doctor: IteroFullRxDoctor | null;
    raw: Record<string, unknown>;
}

export type IteroAssetType = 'IDE' | 'IMAGE_GALLERY' | 'SCREEN_CAPTURE';

export type IteroAssetResult =
    | { kind: 'ready'; blob: Blob; contentDisposition: string | null; contentType: string | null }
    | { kind: 'generating' }
    | { kind: 'empty' };

interface ApiEnvelope<T> {
    success: boolean;
    data: T;
}

const ITERO_BROWSE_TIMEOUT_MS = 30_000;
// Large iDE zips stream via backend and integrations: keep this >= their download timeouts.
const ITERO_DOWNLOAD_TIMEOUT_MS = 600_000;
const HTTP_ACCEPTED = 202;
const HTTP_NO_CONTENT = 204;

const orderPath = (practiceId: string, rxId: string) =>
    `${iteroPath(practiceId)}/orders/${encodeURIComponent(rxId)}`;

export async function searchIteroPatients(
    practiceId: string,
    params: IteroPatientsParams
): Promise<IteroPatientPage> {
    const response = await apiV2.get<ApiEnvelope<IteroPatientPage>>(
        `${iteroPath(practiceId)}/patients`,
        { params, timeout: ITERO_BROWSE_TIMEOUT_MS }
    );
    return response.data.data;
}

export async function listIteroOrders(
    practiceId: string,
    params: IteroOrdersParams
): Promise<IteroOrderPage> {
    const response = await apiV2.get<ApiEnvelope<IteroOrderPage>>(
        `${iteroPath(practiceId)}/orders`,
        { params, timeout: ITERO_BROWSE_TIMEOUT_MS }
    );
    return response.data.data;
}

export async function fetchIteroOrder(practiceId: string, rxId: string): Promise<IteroOrder> {
    const response = await apiV2.get<ApiEnvelope<{ order: IteroOrder }>>(
        orderPath(practiceId, rxId),
        { timeout: ITERO_BROWSE_TIMEOUT_MS }
    );
    return response.data.data.order;
}

export async function fetchIteroRx(practiceId: string, rxId: string): Promise<IteroFullRx> {
    const response = await apiV2.get<ApiEnvelope<{ rx: IteroFullRx }>>(
        `${orderPath(practiceId, rxId)}/rx`,
        { timeout: ITERO_BROWSE_TIMEOUT_MS }
    );
    return response.data.data.rx;
}

function headerString(headers: unknown, name: string): string | null {
    if (typeof headers !== 'object' || headers === null) return null;
    const value = (headers as Record<string, unknown>)[name];
    return typeof value === 'string' ? value : null;
}

export interface IteroDownloadProgress {
    loadedBytes: number;
    totalBytes: number | null;
}

export interface IteroAssetDownloadOptions {
    signal?: AbortSignal;
    onProgress?: (progress: IteroDownloadProgress) => void;
}

// 202 and 204 carry no file: iTero is still building it, or the order has none.
export async function downloadIteroAsset(
    practiceId: string,
    rxId: string,
    assetType: IteroAssetType,
    { signal, onProgress }: IteroAssetDownloadOptions = {}
): Promise<IteroAssetResult> {
    try {
        const response = await apiV2.get<Blob>(
            `${orderPath(practiceId, rxId)}/assets/${assetType}`,
            {
                responseType: 'blob',
                timeout: ITERO_DOWNLOAD_TIMEOUT_MS,
                signal,
                onDownloadProgress: onProgress
                    ? (event) =>
                          onProgress({
                              loadedBytes: event.loaded,
                              totalBytes: event.total || null
                          })
                    : undefined
            }
        );
        if (response.status === HTTP_ACCEPTED) return { kind: 'generating' };
        const body: unknown = response.data;
        if (response.status === HTTP_NO_CONTENT || !(body instanceof Blob) || body.size === 0) {
            return { kind: 'empty' };
        }
        return {
            kind: 'ready',
            blob: response.data,
            contentDisposition: headerString(response.headers, 'content-disposition'),
            contentType: headerString(response.headers, 'content-type')
        };
    } catch (error) {
        throw await parseBlobExportError(error);
    }
}
